import React, { useEffect, useState } from 'react';
import BlockingOverlay from '../components/overlay';
import SuccessOverlay from '../components/successOverlay';
import useApi from '../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import PayPalButtonComponent from '../components/paypalButton'
import usePageSeo from '../utils/usePageSeo';
import { notifyAdsEligibilityChanged } from '../utils/adsEligibility';

export default function PremiumPage() {
  usePageSeo({
    title: 'Premium Plans',
    description: 'Unlock unlimited uploads, premium themes, and ad-free reading with Poppyruz Premium.',
    path: '/pricing',
  });
  const [region, setRegion] = useState('IN'); // default to India
  const [price, setPrice] = useState('₹149');
  const { request } = useApi();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const [isAuthenticated,setIsAuthenticated] = useState(false);

  const handlePayPalSuccess = () => {
    localStorage.setItem('is_premium', 1);
    notifyAdsEligibilityChanged();
    setProcessing(false);
    setShowSuccess(true);
  };

  const handlePayPalError = () => {
    setProcessing(false);
    alert('Something went wrong with the payment. Please contact support.');
  };

  useEffect(() => {
    const detectRegion = async () => {
      try {
        const user = localStorage.getItem('user_id');
        if (user !== null){
          setIsAuthenticated(true);
        }
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        const countryCode = data.country_code;
        switch (countryCode) {
          case 'IN':
            setRegion('IN');
            setPrice('₹149');
            break;
          case 'US':
            setRegion('US');
            setPrice('$5');
            break;
          default:
            setRegion('INTL');
            setPrice('$5');
        }
      } catch (err) {
        console.error('Geo detection failed', err);
        // Fallback to navigator.language
        const lang = navigator.language || '';
        if (lang.includes('en-US')) {
          setRegion('US');
          setPrice('$5');
        } else {
          setRegion('IN');
          setPrice('₹149');
        }
      }
    };
    detectRegion();
  }, []);

  const handleSubscribe = async () => {
    if (region === 'IN'){
      setLoading(true);
      try {
        const user_id = localStorage.getItem('user_id');
        const res = await request({
            method: 'post',
            url: `/api/v1/users/order/`,
            data:{
              user_id: user_id,
              payment_gateway: 'razorpay',
              payment_type: 'subscription',
              amount: 149,
              currency: 'INR'
            }
          });
      const order_id = res?.data?.order_id;
      if (!order_id) throw new Error('Order ID not received');
      const options = {
        key: "rzp_live_plgh0zlUjWermJ",
        amount: 14900, 
        currency: "INR",
        name: "Poppyruz",
        description: "Premium Upgrade",
        image: "/logo192.png", 
        order_id: order_id,
        handler: async function (response) {
          try {
            setLoading(true);
            const res = await request({
            method: 'post',
            url: `/api/v1/users/razorpay_order/`,
            data:{
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }
          });
          localStorage.setItem('is_premium', 1);
          notifyAdsEligibilityChanged();
          console.log('Payment Success:', response);
          alert('Payment successful! Your premium will be activated shortly.');
          navigate('/');
          } catch (err) {
            console.error('Verification failed:', err);
            alert('Payment succeeded but verification failed. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: localStorage.getItem('name'),
          email: localStorage.getItem('email'),
          contact: '',
        },
        theme: {
          color: "#7c3aed",
        },
      };
      const razor = new window.Razorpay(options);
      razor.open();
    } catch (err) {
      console.error('Razorpay error:', err);
      alert('Something went wrong while initiating payment.');
    } finally {
      setLoading(false);
    }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mt-12 text-center">
      <h1 className="text-4xl font-bold text-gray-800 mb-4">Go Premium</h1>
      <p className="text-gray-600 text-lg mb-10">Remove Ads, unlimited uploads, and more.</p>

      {/* Benefits Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10 text-left">
        <div className="bg-white border p-4 rounded shadow">
          <h3 className="font-semibold text-gray-800 mb-2">Unlimited Book Uploads</h3>
          <p className="text-gray-600 text-sm">Say goodbye to the 10-book limit on free accounts.</p>
        </div>
        {/*<div className="bg-white border p-4 rounded shadow">
          <h3 className="font-semibold text-gray-800 mb-2">Custom Theme Uploads</h3>
          <p className="text-gray-600 text-sm">Personalize your reading experience with your own visuals and audio.</p>
        </div>*/}
        <div className="bg-white border p-4 rounded shadow">
          <h3 className="font-semibold text-gray-800 mb-2">Remove Ads</h3>
          <p className="text-gray-600 text-sm">Get rid of annoying advertisements.</p>
        </div>
        {/*<div className="bg-white border p-4 rounded shadow">
          <h3 className="font-semibold text-gray-800 mb-2">Unlock Premium Themes</h3>
          <p className="text-gray-600 text-sm">Access beautifully designed themes crafted by Poppyruz.</p>
        </div>*/}
      </div>

      {/* Pricing & CTA */}
      <div className="bg-purple-100 border border-purple-300 rounded-lg p-6 inline-block">
        <h2 className="text-2xl font-bold text-purple-800 mb-2">One-Time Payment</h2>
        <p className="text-purple-700 text-xl font-semibold mb-4">{price}</p>
          {!isAuthenticated ? (                                   
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Please <span className="font-semibold">log in or sign up</span> to purchase Premium.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 bg-violet-900 text-white rounded hover:bg-violet-950"
                >
                  Home
                </button>
              </div>
            </div>
          ) : region === 'IN' ? (
              <button
                onClick={handleSubscribe}
                className={`px-6 py-3 rounded transition text-white ${
                  loading ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
                }`}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Subscribe Now'}
              </button>
            ) : (
              <PayPalButtonComponent 
              user_id={localStorage.getItem('user_id')}  
              onSuccess={handlePayPalSuccess} 
              onError={handlePayPalError}
              onProcessingChange={setProcessing}/>
            )}
        <p className="text-sm text-gray-500 mt-2 italic">One-time only. No hidden charges. Lifetime access.</p>
      </div>
      {/* overlay while PayPal is capturing card method */}
      {processing && <BlockingOverlay/>}
      {/* success modal for paypal login method */}
      {showSuccess && (
        <SuccessOverlay
          message="Payment successful! Your premium will be activated shortly."
          duration={2000}
          onDone={() => {
            setShowSuccess(false);
            navigate('/');
          }}
        />
      )}
    </div>
  );
}
